import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TIME_SLOTS, VEHICLE_SIZES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = "w-full rounded-none border-white/15 bg-zinc-900 text-white placeholder:text-zinc-600 focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary h-12";

export const Booking = ({ services, addons = [], selectedService, setSelectedService }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", vehicle: "", vehicle_size: "sedan", address: "", access_notes: "", notes: "" });
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [occupied, setOccupied] = useState([]);

  const toggleAddon = (value) =>
    setSelectedAddons((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const svcSlots = services.find((s) => s.slug === selectedService)?.slots || 1;

  const selectedSvc = services.find((s) => s.slug === selectedService);
  const sizeObj = VEHICLE_SIZES.find((v) => v.value === form.vehicle_size);
  const addonTotal = addons.filter((a) => selectedAddons.includes(a.value)).reduce((sum, a) => sum + a.price, 0);
  const total = selectedSvc ? selectedSvc.price + (sizeObj?.surcharge || 0) + addonTotal : null;

  const slotDisabled = (idx) => {
    if (idx + svcSlots > TIME_SLOTS.length) return true;
    for (let k = idx; k < idx + svcSlots; k++) {
      if (occupied.includes(k)) return true;
    }
    return false;
  };

  useEffect(() => {
    if (!date) { setOccupied([]); return; }
    axios.get(`${API}/availability`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => setOccupied(r.data.occupied || []))
      .catch(() => setOccupied([]));
  }, [date]);

  useEffect(() => {
    if (!time) return;
    const idx = TIME_SLOTS.indexOf(time);
    if (idx === -1 || slotDisabled(idx)) setTime("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, occupied]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.vehicle || !form.address || !selectedService || !date || !time) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const svc = services.find((s) => s.slug === selectedService);
      await axios.post(`${API}/bookings`, {
        ...form,
        service: svc ? svc.name : selectedService,
        addons: selectedAddons,
        date: format(date, "yyyy-MM-dd"),
        time,
      });
      setDone(true);
      toast.success("Booking requested — we'll confirm by phone shortly.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 409 || status === 400) {
        toast.error(err.response.data?.detail || "Please choose another time.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="booking" className="relative overflow-hidden border-t border-white/10 bg-black px-6 py-24 md:px-12 md:py-32">
      <div className="glow-teal left-[-6%] top-1/4 h-[420px] w-[420px] bg-teal-500/18" />
      <div className="glow-teal right-[-6%] bottom-1/4 h-[360px] w-[360px] bg-cyan-500/12" />
      <div className="relative mx-auto grid max-w-[1500px] grid-cols-1 gap-16 md:grid-cols-12">
        <div className="md:col-span-4">
          <span className="font-mono-x text-xs uppercase tracking-[0.3em] text-primary">/ Reserve</span>
          <h2 className="mt-6 font-cabinet text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl">
            <span className="gradient-text">Book Your<br />Detail</span>
          </h2>
          <p className="mt-8 max-w-sm text-base leading-relaxed text-zinc-400">
            Choose a service, pick a date and we'll confirm your appointment within the hour. Limited slots each day to protect our standard.
          </p>
          <div className="mt-10 space-y-4 font-mono-x text-xs uppercase tracking-widest text-zinc-500">
            <p className="flex items-center gap-3"><span className="h-1 w-6 bg-primary" /> Mon – Sat · 8AM to 8PM</p>
            <p className="flex items-center gap-3"><span className="h-1 w-6 bg-primary" /> 100% mobile — we come to your location</p>
          </div>
        </div>

        <div className="md:col-span-8">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="booking-success"
              className="flex min-h-[400px] flex-col items-center justify-center border border-primary/40 bg-card p-12 text-center"
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check size={30} />
              </div>
              <h3 className="mt-8 font-cabinet text-3xl font-bold uppercase tracking-tight text-white">Request Received</h3>
              <p className="mt-4 max-w-md text-zinc-400">
                Thanks, {form.name.split(" ")[0]}. Your slot is pencilled in for {date && format(date, "MMM d")} at {time}. We'll call {form.phone} to confirm.
              </p>
              <button
                data-testid="booking-reset"
                onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", vehicle: "", vehicle_size: "sedan", address: "", access_notes: "", notes: "" }); setSelectedAddons([]); setDate(null); setTime(""); setSelectedService(""); }}
                className="mt-8 border border-white/20 px-6 py-3 font-mono-x text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:border-primary hover:text-primary"
              >
                Book another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 gap-6 border border-white/10 bg-card p-6 md:grid-cols-2 md:p-10">
              <div className="space-y-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Full Name *</Label>
                <Input data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Jordan Miles" className={field} />
              </div>
              <div className="space-y-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Phone *</Label>
                <Input data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="(555) 018-2245" className={field} />
              </div>
              <div className="space-y-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Email *</Label>
                <Input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={field} />
              </div>
              <div className="space-y-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Vehicle *</Label>
                <Input data-testid="input-vehicle" value={form.vehicle} onChange={update("vehicle")} placeholder="2022 Honda Civic" className={field} />
              </div>

              <div className="space-y-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Vehicle Size *</Label>
                <Select value={form.vehicle_size} onValueChange={(v) => setForm({ ...form, vehicle_size: v })}>
                  <SelectTrigger data-testid="select-vehicle-size" className={field}>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent data-lenis-prevent className="rounded-none border-white/15 bg-zinc-900 text-white">
                    {VEHICLE_SIZES.map((v) => (
                      <SelectItem key={v.value} value={v.value} data-testid={`size-${v.value}`} className="focus:bg-primary focus:text-primary-foreground">
                        {v.label}{v.surcharge ? ` (+$${v.surcharge})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Service Address *</Label>
                <Input data-testid="input-address" value={form.address} onChange={update("address")} placeholder="Street, City, State ZIP — where we come to you" className={field} />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Access Notes</Label>
                <Input data-testid="input-access" value={form.access_notes} onChange={update("access_notes")} placeholder="Driveway / parking, water & power access, gate code, etc." className={field} />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Service *</Label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger data-testid="select-service" className={field}>
                    <SelectValue placeholder="Select a package" />
                  </SelectTrigger>
                  <SelectContent data-lenis-prevent className="rounded-none border-white/15 bg-zinc-900 text-white">
                    {services.map((s) => (
                      <SelectItem key={s.slug} value={s.slug} data-testid={`option-${s.slug}`} className="focus:bg-primary focus:text-primary-foreground">
                        {s.name} — ${s.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="select-date" className={`${field} flex items-center justify-between px-3 text-left ${date ? "text-white" : "text-zinc-600"}`}>
                      {date ? format(date, "PPP") : "Pick a date"}
                      <CalendarIcon size={16} className="text-zinc-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent data-lenis-prevent className="w-auto rounded-none border-white/15 bg-zinc-900 p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || d.getDay() === 0} initialFocus className="text-white" />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Time *</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="select-time" className={field}>
                    <SelectValue placeholder="Select a slot" />
                  </SelectTrigger>
                  <SelectContent data-lenis-prevent className="rounded-none border-white/15 bg-zinc-900 text-white">
                    {TIME_SLOTS.map((t, idx) => {
                      const disabled = slotDisabled(idx);
                      return (
                        <SelectItem key={t} value={t} disabled={disabled} className="focus:bg-primary focus:text-primary-foreground">
                          {t}{disabled ? " — unavailable" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Notes</Label>
                <Textarea data-testid="input-notes" value={form.notes} onChange={update("notes")} placeholder="Anything we should know about the vehicle?" className={`${field} h-24`} />
              </div>

              {addons.length > 0 ? (
                <div className="space-y-2 md:col-span-2">
                  <Label className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">Add-Ons (optional)</Label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {addons.map((a) => {
                      const on = selectedAddons.includes(a.value);
                      return (
                        <button
                          type="button"
                          key={a.value}
                          data-testid={`addon-toggle-${a.value}`}
                          onClick={() => toggleAddon(a.value)}
                          className={`flex items-center justify-between border px-4 py-3 text-left transition-colors duration-200 ${on ? "border-primary bg-primary/10" : "border-white/15 bg-zinc-900 hover:border-white/30"}`}
                        >
                          <span className="text-sm text-white">{a.label}</span>
                          <span className={`font-mono-x text-xs ${on ? "text-primary" : "text-zinc-500"}`}>+${a.price}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {selectedSvc ? (
                <div data-testid="booking-total" className="flex items-center justify-between border border-primary/30 bg-zinc-900 px-5 py-4 md:col-span-2">
                  <span className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-400">
                    {selectedSvc.name} · {sizeObj?.label}{selectedAddons.length ? ` · +${selectedAddons.length} add-on${selectedAddons.length > 1 ? "s" : ""}` : ""}
                  </span>
                  <span className="font-cabinet text-3xl font-black gradient-text">${total}</span>
                </div>
              ) : null}

              <button
                type="submit"
                data-testid="submit-booking"
                disabled={loading}
                className="group mt-2 flex items-center justify-center gap-3 gradient-fill px-8 py-4 font-mono-x text-xs uppercase tracking-widest text-black transition-opacity duration-300 hover:opacity-90 disabled:opacity-60 md:col-span-2"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? "Sending..." : "Request Appointment"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
